import React, { useEffect, useRef, useState } from 'react';
import { MapContainer, TileLayer, useMap } from 'react-leaflet';
import L from 'leaflet';
import { classifyVozidlo } from './VozidloDlazdice';
import { vozidloColor } from '../../utils/vozidloColor';

const FRENSTAT_CENTER = [49.5475, 18.2122];

// Padding pro fitBounds.
//   Desktop: vlevo 372 px = 340 sidebar + 32 breathing (markery napravo od
//     karty řidiče, ať se nepřekrývají).
//   Mobile: jen drobný symetrický breathing — avatary v levém dolním rohu
//     jsou malé a poloprůhledné, marker pod nimi nikomu nevadí. Důležitější
//     je využít celou plochu mobilu, aby auta nebyla zbytečně namačkaná.
const FIT_PADDING_TOP_LEFT_DESKTOP = [372, 60];
const FIT_PADDING_TOP_LEFT_MOBILE = [24, 24];
const FIT_PADDING_BOTTOM_RIGHT_DESKTOP = [60, 60];
const FIT_PADDING_BOTTOM_RIGHT_MOBILE = [24, 24];

// Granularita pro detekci změny bodů — ~100 m. Auta jezdící uvnitř tohoto
// bucketu nevyvolají refit (jinak by viewport poskakoval s každým Reverb fixem).
const POSITION_PRECISION = 3;

// Po této době bez user interakce (pan / zoom) se auto-fit znovu zapne. Klik
// na LIVE badge re-fituje okamžitě bez ohledu na timer.
const AUTOFIT_RESUME_AFTER_MS = 60_000;

/**
 * Vrátí rotaci + mirror flag pro car ikonu tak, aby nikdy nebyla vzhůru nohama.
 *   - Lucide car siluetka směřuje východ (90°), takže rotace = smer - 90
 *   - Pokud výsledek leží mimo (-90°, 90°), auto by mířilo na západ a kola by
 *     byla nahoře. Místo toho rotaci shiftneme o 180° a zrcadlíme přes scaleX(-1)
 *     → kola vždy dole, auto natočené správným směrem.
 */
function rotationFor(smer) {
    if (!Number.isFinite(smer)) return { rotate: 0, scaleX: 1 };
    let rot = (((smer - 90) % 360) + 540) % 360 - 180;
    let scaleX = 1;
    if (rot > 90) { rot -= 180; scaleX = -1; }
    else if (rot < -90) { rot += 180; scaleX = -1; }
    return { rotate: rot, scaleX };
}

// Sídlo provozovny FofrLama — Náměstí Míru 20, Frenštát pod Radhoštěm.
// Souřadnice sjednoceny s config/flotila.php (geofence_centrum_lat/lng).
const BISTRO_LAT_LNG = [49.5478281, 18.2119512];

function getMapyApiKey() {
    return document.querySelector('meta[name="mapy-api-key"]')?.content || '';
}

// Marker animation — lineární tween "vždy běží".
//
// Každý fix přepne cíl tweenu. Duration = wall-clock arrival interval
// (kolik reálných sekund uplynulo od přijetí předchozího fixu), NE
// timestamp z GPS — díky tomu segmenty plynule navazují i při síťovém
// jitteru (Reverb event 5 s timestamp, ale doletí 7 s po předchozím).
// Lineární interpolace (bez easingu) → uvnitř segmentu konstantní rychlost,
// žádné slow-down na konci, žádný "rozjezd-zastavení".
//
// Trade-off: marker je vizuálně ~1 fix-interval pozadu reality. UX dispečera
// je řádově plynulejší než varianta s frontou (která mezi segmenty tvořila
// pauzy, když Reverb arrival byl pomalejší než GPS interval).
const SEGMENT_MS_MIN = 800;     // bursty Reverb (multiple events per second)
const SEGMENT_MS_MAX = 12000;   // pomalé fixy (vozidlo offline / weak signal)
const SEGMENT_MS_FALLBACK = 2000;

// Nový fix dál než tohle od posledního = teleport (špatná data, restart
// trackeru). Jen snap, žádný tween.
const TELEPORT_THRESHOLD_M = 1500;

// Haversine vzdálenost dvou bodů v metrech — pro detekci teleportu.
const haversineM = (lat1, lng1, lat2, lng2) => {
    const R = 6371000;
    const toRad = (x) => x * Math.PI / 180;
    const dLat = toRad(lat2 - lat1);
    const dLng = toRad(lng2 - lng1);
    const a = Math.sin(dLat / 2) ** 2
        + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
    return 2 * R * Math.asin(Math.sqrt(a));
};

/**
 * Vnitřní layer — má přístup k mapě přes useMap, spravuje markery
 * imperativně. Při změně polohy plynule interpoluje (rAF) místo
 * teleportu, takže marker "ujíždí" mezi updaty z Reverbu / poller-u.
 */
/**
 * Statický pin sídla provozovny (FofrLama Bistro). Teardrop tvar v brand
 * oranžové, ikonka utensils. Pod markery aut (zIndexOffset záporný), aby
 * jezdící auto nebylo skryto pod pinem, když odjíždí z bistra.
 */
/**
 * Po mountu / změně velikosti kontejneru řekne Leafletu, ať si přepočítá
 * cachované rozměry. Bez toho přepnutí desktop ↔ mobile nechá mapu na
 * staré velikosti → tiles a markery na špatných pozicích → vypadá to
 * černě.
 *
 * Implementace záměrně bez window resize listeneru — tomu už naslouchá
 * Leaflet sám (Map._onResize), zdvojené volání invalidateSize během
 * resize gesta jen plodí jitter a teoreticky riskuje observer-loop.
 * ResizeObserver na kontejneru pokryje všechny případy, kdy se rozměr
 * změnil bez window resize (toggle isMobile, fullscreen, …).
 */
function InvalidateOnResize() {
    const map = useMap();
    useEffect(() => {
        if (!map) return;
        const container = map.getContainer();
        if (!container) return;

        let lastW = container.clientWidth;
        let lastH = container.clientHeight;
        let raf = null;

        const tick = () => {
            raf = null;
            const w = container.clientWidth;
            const h = container.clientHeight;
            if (w === lastW && h === lastH) return; // bez změny → no-op
            lastW = w;
            lastH = h;
            try { map.invalidateSize({ pan: false, animate: false }); } catch {}
        };

        let ro = null;
        try {
            ro = new ResizeObserver(() => {
                if (raf) return;
                raf = requestAnimationFrame(tick);
            });
            ro.observe(container);
        } catch {}

        // Pojistka po mountu (mount může proběhnout dřív než nadefinovaný
        // layout). Single-shot — bez window listeneru.
        const initial = setTimeout(() => { lastW = -1; lastH = -1; tick(); }, 100);

        return () => {
            if (raf) cancelAnimationFrame(raf);
            clearTimeout(initial);
            try { ro?.disconnect(); } catch {}
        };
    }, [map]);
    return null;
}

function BistroLayer() {
    const map = useMap();
    const markerRef = useRef(null);
    useEffect(() => {
        if (markerRef.current) return;
        const SVG_HOME = '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M15 21v-8a1 1 0 0 0-1-1h-4a1 1 0 0 0-1 1v8"/><path d="M3 10a2 2 0 0 1 .709-1.528l7-5.999a2 2 0 0 1 2.582 0l7 5.999A2 2 0 0 1 21 10v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/></svg>';
        const html = `
            <div style="position:relative;width:36px;height:36px;">
                <div class="flotila-bistro-pin">${SVG_HOME}</div>
            </div>
        `;
        const icon = L.divIcon({
            html,
            className: 'flotila-bistro-divicon',
            iconSize: [36, 36],
            // Anchor v levém dolním rohu pinu (špička teardropu) — kvůli
            // rotate(-45deg) leží špička právě tam.
            iconAnchor: [4, 32],
        });
        const m = L.marker(BISTRO_LAT_LNG, { icon, zIndexOffset: -100, interactive: false });
        m.addTo(map);
        markerRef.current = m;
        return () => {
            try { m.remove(); } catch {}
            markerRef.current = null;
        };
    }, [map]);
    return null;
}

/**
 * Rozvozové zóny jako tichá vrstva pod auty — přerušovaný obrys v barvě zóny,
 * skoro průhledná výplň, neklikatelné (mapa patří autům). Vnější zóny se
 * kreslí dřív, ať vnitřní obrys leží navrchu.
 */
function ZonyLayer({ zony = [] }) {
    const map = useMap();
    useEffect(() => {
        const vrstvy = [...zony]
            .filter((z) => Array.isArray(z.polygon) && z.polygon.length >= 3)
            .sort((a, b) => (b.poradi ?? 0) - (a.poradi ?? 0))
            .map((z) => L.polygon(z.polygon, {
                color: z.barva || '#0CC0DF', weight: 1.5, dashArray: '6 5',
                fillColor: z.barva || '#0CC0DF', fillOpacity: 0.05, interactive: false,
            }).addTo(map));
        return () => vrstvy.forEach((v) => { try { v.remove(); } catch {} });
    }, [map, zony]);
    return null;
}

function MarkersLayer({ vozidla, selectedId, onMarkerClick, ridiciMap }) {
    const map = useMap();
    const markersRef = useRef(new Map());
    const rafIdRef = useRef(null);

    // Přijde nový fix → tween z aktuální zobrazené polohy do nové cílové.
    // Pokud běží předchozí tween, jeho aktuální pozice slouží jako start
    // (žádný skok zpět). Duration = wall-clock interval mezi PŘÍCHODY fixů
    // (NE mezi GPS timestamps) — díky tomu marker jezdí kontinuálně, i když
    // má Reverb síťový jitter. Lineární interpolace = konstantní rychlost
    // v rámci segmentu, žádné slow-down na konci.
    //
    // `_meta` (smer + rychlost) využívá rAF loop pro extrapolaci po skončení
    // tweenu — auto „neskočí stát", ale plynule pokračuje známým směrem
    // a rychlostí, dokud nedorazí další fix (max 10 s, viz MAX_EXTRA_MS).
    const updateAnchor = (marker, lat, lng, fixIso, rychlostKmh, smer) => {
        const now = performance.now();
        const fixMs = fixIso ? Date.parse(fixIso) : Date.now();
        const prev = marker._anchor;

        // Vždy aktualizuj _meta — extrapolace používá nejčerstvější známé.
        marker._meta = {
            smer: Number.isFinite(Number(smer)) ? Number(smer) : null,
            rychlost: Math.max(0, Number(rychlostKmh) || 0),
        };

        // První fix — snap, žádný tween.
        if (!prev) {
            marker.setLatLng([lat, lng]);
            marker._anchor = { lat, lng, fixMs, arrivedAt: now };
            marker._tween = null;
            marker._extra = null;
            return;
        }

        // Teleport — vzdálenost > threshold = bug data / restart trackeru.
        const jumpM = haversineM(prev.lat, prev.lng, lat, lng);
        if (jumpM > TELEPORT_THRESHOLD_M) {
            marker.setLatLng([lat, lng]);
            marker._anchor = { lat, lng, fixMs, arrivedAt: now };
            marker._tween = null;
            marker._extra = null;
            return;
        }

        // Wall-clock arrival interval — to je čas, který má marker k dispozici
        // pro tento segment. Když přicházejí fixy průměrně každých 5 s, segment
        // trvá 5 s a další fix přijde právě jak segment dokončí → kontinuální
        // pohyb. Když Reverb skočí (1 s nebo 12 s), segment se přizpůsobí.
        const intervalMs = now - prev.arrivedAt;
        const durationMs = intervalMs > 0
            ? Math.min(SEGMENT_MS_MAX, Math.max(SEGMENT_MS_MIN, intervalMs))
            : SEGMENT_MS_FALLBACK;

        // Start = aktuálně zobrazená poloha (může být uprostřed předchozího
        // tweenu); marker se nikdy nevrací zpět.
        const cur = marker.getLatLng();
        marker._tween = {
            fromLat: cur.lat,
            fromLng: cur.lng,
            toLat: lat,
            toLng: lng,
            startPerfMs: now,
            durationMs,
        };
        marker._anchor = { lat, lng, fixMs, arrivedAt: now };
        marker._extra = null;
    };

    // rAF loop — tween → extrapolace.
    //   1) Probíhající tween: lineárně interpoluje k cílové pozici.
    //   2) Po dokončení tweenu (t >= 1): pokud má auto rychlost > 0 a známý
    //      směr, pokračujeme „dead reckoning" extrapolací podle posledně
    //      známé rychlosti + bearingu. Auto plynule jede dál místo skoku stop.
    //      Cap MAX_EXTRA_MS (10 s) — bez něj by při výpadku Reverbu auto
    //      odjelo do nekonečna pryč od reality.
    useEffect(() => {
        const MAX_EXTRA_MS = 10000;

        const tick = () => {
            const now = performance.now();
            for (const marker of markersRef.current.values()) {
                const tw = marker._tween;
                if (tw) {
                    const t = Math.min(1, (now - tw.startPerfMs) / tw.durationMs);
                    const lat = tw.fromLat + (tw.toLat - tw.fromLat) * t;
                    const lng = tw.fromLng + (tw.toLng - tw.fromLng) * t;
                    marker.setLatLng([lat, lng]);
                    if (t >= 1) {
                        marker._tween = null;
                        const meta = marker._meta;
                        if (meta?.rychlost > 0 && meta.smer != null) {
                            marker._extra = {
                                startLat: tw.toLat,
                                startLng: tw.toLng,
                                startPerfMs: now,
                                speedMs: meta.rychlost / 3.6,
                                bearingRad: (meta.smer * Math.PI) / 180,
                            };
                        }
                    }
                    continue;
                }
                const ex = marker._extra;
                if (!ex) continue;
                const elapsedSec = (now - ex.startPerfMs) / 1000;
                if (elapsedSec * 1000 > MAX_EXTRA_MS) {
                    marker._extra = null;
                    continue;
                }
                const distM = ex.speedMs * elapsedSec;
                const dLat = (distM * Math.cos(ex.bearingRad)) / 111320;
                const dLng =
                    (distM * Math.sin(ex.bearingRad)) /
                    (111320 * Math.cos((ex.startLat * Math.PI) / 180));
                marker.setLatLng([ex.startLat + dLat, ex.startLng + dLng]);
            }
            rafIdRef.current = requestAnimationFrame(tick);
        };
        rafIdRef.current = requestAnimationFrame(tick);
        return () => {
            if (rafIdRef.current) cancelAnimationFrame(rafIdRef.current);
        };
    }, [map]);

    const buildIcon = (vozidlo, isSelected) => {
        const status = classifyVozidlo(vozidlo);
        const driverColor = vozidloColor(vozidlo.id);
        const inServis = vozidlo.stav === 'servis' || vozidlo.aktivni_servis;
        const selectionRing = isSelected ? 'box-shadow:0 0 0 3px rgba(12,192,223,0.7),0 4px 12px rgba(0,0,0,0.5);' : '';

        // SERVIS → wrench ikona v oranžovém pozadí, ostatní stavy ignorujeme.
        if (inServis) {
            const SVG_WRENCH = '<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a3.41 3.41 0 0 1-4.82-4.82l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/></svg>';
            const html = `<div class="flotila-marker service${isSelected ? ' selected' : ''}" style="color:#fff;${selectionRing}">${SVG_WRENCH}</div>`;
            return L.divIcon({ html, className: 'flotila-divicon', iconSize: [28, 28], iconAnchor: [14, 14] });
        }

        // DRIVING → šipka, výplň = barva řidiče, neon „trail" efekt z POS VD:
        //   - vnitřní zář (0 0 4px) = ostrý hot core
        //   - široký halo (0 0 14px @ 0.55) = motion blur dojem
        //   - depth shadow (0 2px 4px black) = oddělení od mapy
        // Bílý obrys 1.5px udrží šipku čitelnou na libovolném pozadí.
        if (status === 'driving') {
            const smerNum = Number(vozidlo.smer);
            const arrowRotate = Number.isFinite(smerNum) ? smerNum : 0;
            const trail = `drop-shadow(0 0 4px ${driverColor}) drop-shadow(0 0 14px ${driverColor}8c) drop-shadow(0 2px 4px rgba(0,0,0,0.7))`;
            const SVG = `<svg xmlns="http://www.w3.org/2000/svg" width="26" height="26" viewBox="0 0 24 24" fill="${driverColor}" stroke="white" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" style="filter:${trail};display:block;transform:rotate(${arrowRotate}deg);transition:transform 0.6s ease-out;">
                <path d="M12 2 L20 21 L12 16 L4 21 Z"/>
            </svg>`;
            return L.divIcon({ html: SVG, className: 'flotila-divicon', iconSize: [26, 26], iconAnchor: [13, 13] });
        }

        // IDLE / PARKED → autíčko, šedá výplň, obrys = barva řidiče.
        // Rotace podle směru (s anti-upside-down mirrorem) — i parkující auto
        // si nese poslední známé natočení.
        const { rotate, scaleX } = rotationFor(Number(vozidlo.smer));
        const carShadow = `drop-shadow(0 2px 3px rgba(0,0,0,0.65))`;
        const SVG = `<svg xmlns="http://www.w3.org/2000/svg" width="26" height="26" viewBox="0 0 24 24" fill="#6B7280" stroke="${driverColor}" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" style="filter:${carShadow};display:block;">
            <path d="M14 16H9m10 0h3v-3.15a1 1 0 0 0-.84-.99L16 11l-2.7-3.6a1 1 0 0 0-.8-.4H5.24a2 2 0 0 0-1.8 1.1l-.8 1.63A6 6 0 0 0 2 12.42V15.5a.5.5 0 0 0 .5.5H5"/>
            <circle cx="6.5" cy="16.5" r="2.5" fill="#0A0A0C"/>
            <circle cx="16.5" cy="16.5" r="2.5" fill="#0A0A0C"/>
        </svg>`;
        const html = `<div style="width:26px;height:26px;display:flex;align-items:center;justify-content:center;transform:rotate(${rotate}deg) scaleX(${scaleX});transition:transform 0.6s ease-out;${selectionRing}">${SVG}</div>`;
        return L.divIcon({ html, className: 'flotila-divicon', iconSize: [26, 26], iconAnchor: [13, 13] });
    };

    useEffect(() => {
        const existing = markersRef.current;
        const seen = new Set();

        // Render JEN vozidla s přiřazeným řidičem — bez řidiče auto nereprezentuje
        // aktivního rozvozce, nepatří do live přehledu. Sjednoceno s POS VD.
        for (const v of vozidla) {
            if (!v.aktualni_ridic_id) continue;
            if (!v.lat || !v.lng) continue;
            seen.add(v.id);
            const isSel = v.id === selectedId;
            let marker = existing.get(v.id);

            const status = classifyVozidlo(v);
            const smer = Number.isFinite(Number(v.smer)) ? Number(v.smer) : null;

            if (marker) {
                updateAnchor(marker, v.lat, v.lng, v.zaznameno, v.rychlost_kmh, v.smer);
                const last = marker._fkApplied || {};
                // Status / selection / smer / servis = re-build celé ikony.
                // (smer ovlivňuje SVG rotaci, takže setIcon je nutný.)
                const inServis = v.stav === 'servis' || v.aktivni_servis;
                if (last.isSel !== isSel || last.status !== status || last.smer !== smer || last.inServis !== inServis) {
                    marker.setIcon(buildIcon(v, isSel));
                    marker._fkApplied = { isSel, status, smer, inServis };
                }
            } else {
                marker = L.marker([v.lat, v.lng], { icon: buildIcon(v, isSel), zIndexOffset: 1000 });
                marker._fkApplied = { isSel, status, smer, inServis: v.stav === 'servis' || v.aktivni_servis };
                updateAnchor(marker, v.lat, v.lng, v.zaznameno, v.rychlost_kmh, v.smer);
                marker.on('click', () => onMarkerClick?.(v.id));
                marker.addTo(map);
                existing.set(v.id, marker);
            }
        }

        for (const [id, marker] of existing.entries()) {
            if (!seen.has(id)) {
                marker.remove();
                existing.delete(id);
            }
        }
    }, [vozidla, selectedId, map, onMarkerClick]);

    // Při výběru vozidla — jednorázový zoom-in na auto (zoom 14, ať jsou
    // hned vidět i blízké rozvozové body). RozvozyLayer pak může BoundsFitter
    // ještě rozšířit přes objednávky.
    useEffect(() => {
        if (!selectedId) return;
        const v = vozidla.find((x) => x.id === selectedId);
        if (v?.lat && v?.lng) {
            map.setView([v.lat, v.lng], 14, { animate: true });
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedId, map]);

    return null;
}

/**
 * Sleduje user interakce s mapou (pan / zoom). Vrací aktuální „interakční"
 * stav (paused = user nedávno klikl) přes `setPaused` callback. Po
 * AUTOFIT_RESUME_AFTER_MS bez interakce sám reset-uje paused na false.
 * `refitSignal` (např. counter z LIVE badge clicku) okamžitě reset-uje
 * paused → BoundsFitter dostane šanci re-fitnout.
 */
function UserInteractionWatcher({ onPausedChange, refitSignal }) {
    const map = useMap();
    const lastInteractionRef = useRef(0);
    const timerRef = useRef(null);

    useEffect(() => {
        const markInteracted = () => {
            lastInteractionRef.current = performance.now();
            onPausedChange?.(true);
            if (timerRef.current) clearTimeout(timerRef.current);
            timerRef.current = setTimeout(() => {
                const since = performance.now() - lastInteractionRef.current;
                if (since >= AUTOFIT_RESUME_AFTER_MS) {
                    onPausedChange?.(false);
                }
            }, AUTOFIT_RESUME_AFTER_MS + 100);
        };

        // dragstart / zoomstart musí jít z user gesture, ne z programatického
        // setView/fitBounds. Leaflet bohužel emituje stejné eventy pro oboje,
        // takže rozlišujeme přes `_animatingZoom` flag a stamp programmatic
        // calls v BoundsFitter (přes options.flotilaProgrammatic — viz tam).
        const onMoveStart = (e) => {
            // Programmatic fit má `e.flotilaProgrammatic` set z BoundsFitter
            if (e && e.flotilaProgrammatic) return;
            markInteracted();
        };
        const onZoomStart = (e) => {
            if (e && e.flotilaProgrammatic) return;
            markInteracted();
        };

        map.on('dragstart', onMoveStart);
        map.on('zoomstart', onZoomStart);
        return () => {
            map.off('dragstart', onMoveStart);
            map.off('zoomstart', onZoomStart);
            if (timerRef.current) clearTimeout(timerRef.current);
        };
    }, [map, onPausedChange]);

    // refitSignal change = force resume (uživatel klikl na LIVE badge)
    useEffect(() => {
        if (refitSignal == null) return;
        lastInteractionRef.current = 0;
        if (timerRef.current) clearTimeout(timerRef.current);
        onPausedChange?.(false);
    }, [refitSignal, onPausedChange]);

    return null;
}

/**
 * Drží viewport mapy nad aktivními body — auta s řidičem + rozvozové objednávky
 * + objednávky v přípravě. Refit jen když se změní set bodů (rounded ~100 m
 * bucket) → neposkakuje na každý Reverb fix. `paused=true` blokuje refit
 * úplně (uživatel zoomoval/panoval, dej mu prostor).
 *
 * Když je selectedId, fit se omezí na vybrané auto + jeho objednávky.
 */
function BoundsFitter({ vozidla, rozvozy, pripravuje, selectedId, paused, isMobile }) {
    const map = useMap();
    const lastSigRef = useRef('');

    useEffect(() => {
        if (paused) return;

        const points = [];
        if (selectedId) {
            const v = vozidla.find((x) => x.id === selectedId);
            if (v?.lat && v?.lng) points.push([v.lat, v.lng]);
            const r = rozvozy.find((x) => x.vozidlo_id === selectedId);
            if (r) {
                for (const o of (r.objednavky_na_ceste || [])) {
                    if (Number.isFinite(o.lat) && Number.isFinite(o.lng)) points.push([o.lat, o.lng]);
                }
                for (const o of (r.objednavky_dorucene || [])) {
                    if (Number.isFinite(o.lat) && Number.isFinite(o.lng)) points.push([o.lat, o.lng]);
                }
            }
        } else {
            for (const v of vozidla) {
                if (!v.aktualni_ridic_id) continue;
                if (Number.isFinite(v.lat) && Number.isFinite(v.lng)) points.push([v.lat, v.lng]);
            }
            for (const r of rozvozy) {
                for (const o of (r.objednavky_na_ceste || [])) {
                    if (Number.isFinite(o.lat) && Number.isFinite(o.lng)) points.push([o.lat, o.lng]);
                }
                for (const o of (r.objednavky_dorucene || [])) {
                    if (Number.isFinite(o.lat) && Number.isFinite(o.lng)) points.push([o.lat, o.lng]);
                }
            }
            for (const o of (pripravuje || [])) {
                if (Number.isFinite(o.lat) && Number.isFinite(o.lng)) points.push([o.lat, o.lng]);
            }
        }
        if (points.length === 0) return;

        const sig = points
            .map((p) => `${p[0].toFixed(POSITION_PRECISION)},${p[1].toFixed(POSITION_PRECISION)}`)
            .sort()
            .join('|');
        const sigWithSel = `${selectedId || 'none'}::${sig}`;
        if (sigWithSel === lastSigRef.current) return;
        lastSigRef.current = sigWithSel;

        // Označ event jako programmatic, ať UserInteractionWatcher nepokládá
        // re-fit za uživatelovu akci a nepauzoval auto-fit ihned poté.
        const topLeft = isMobile ? FIT_PADDING_TOP_LEFT_MOBILE : FIT_PADDING_TOP_LEFT_DESKTOP;
        const bottomRight = isMobile ? FIT_PADDING_BOTTOM_RIGHT_MOBILE : FIT_PADDING_BOTTOM_RIGHT_DESKTOP;
        const fireProgrammatic = (fn) => {
            const origFire = map.fire.bind(map);
            map.fire = (type, data, ...rest) => origFire(type, { ...(data || {}), flotilaProgrammatic: true }, ...rest);
            try { fn(); } finally { map.fire = origFire; }
        };

        fireProgrammatic(() => {
            if (points.length === 1) {
                map.setView(points[0], 14, { animate: true });
            } else {
                map.fitBounds(points, {
                    paddingTopLeft: topLeft,
                    paddingBottomRight: bottomRight,
                    maxZoom: 15,
                    animate: true,
                });
            }
        });
    }, [vozidla, rozvozy, pripravuje, selectedId, paused, isMobile, map]);

    // Při deselect zresetujeme sig, aby refit přes celý fleet proběhl i kdyby
    // shodou byla stejná sada bodů.
    useEffect(() => {
        if (!selectedId) lastSigRef.current = '';
    }, [selectedId]);

    return null;
}

const formatPredani = (sec) => {
    if (sec == null) return '';
    const m = Math.floor(sec / 60);
    const s = sec % 60;
    return m > 0 ? `${m} min ${s} s` : `${s} s`;
};

/**
 * Layer pro Live rozvozy — markery objednávek (na cestě + nedávno doručené)
 * a dvě polyline per vozidlo: plánovaná trasa (dashed) + aktuální trasa
 * z knihy jízd (solid). Per-auto barva přes vozidloColor(id).
 *
 * Při výběru vozidla zobrazujeme jen jeho rozvozy + trasu, jinak všechny
 * aktivní vozidla. Vrstva je vždy viditelná bez ohledu na zoom — dispečer
 * potřebuje vidět trasy i v overview pohledu, i kdyby šlo o malé tečky.
 */
function RozvozyLayer({ selectedId, rozvozy = [] }) {
    const map = useMap();
    const layerRef = useRef(null);
    const [trasy, setTrasy] = useState({}); // { vozidloId: [[lat,lng], ...] }

    // Init layer group jednou
    useEffect(() => {
        layerRef.current = L.layerGroup().addTo(map);
        return () => {
            if (layerRef.current) {
                layerRef.current.remove();
                layerRef.current = null;
            }
        };
    }, [map]);

    // Při výběru auta — jakmile máme rozvozy, fitBounds přes auto + jeho
    // objednávky (na cestě + doručené v této jízdě). Bez toho je viewport
    // po zoom 14 v MarkersLayer, vzdálené body objednávek zůstanou mimo.
    const lastAutoFitRef = useRef(null);
    useEffect(() => {
        if (!selectedId) { lastAutoFitRef.current = null; return; }
        const r = rozvozy.find((x) => x.vozidlo_id === selectedId);
        if (!r) return;

        const points = [];
        if (Number.isFinite(r.lat) && Number.isFinite(r.lng)) points.push([r.lat, r.lng]);
        for (const o of (r.objednavky_na_ceste || [])) {
            if (Number.isFinite(o.lat) && Number.isFinite(o.lng)) points.push([o.lat, o.lng]);
        }
        for (const o of (r.objednavky_dorucene || [])) {
            if (Number.isFinite(o.lat) && Number.isFinite(o.lng)) points.push([o.lat, o.lng]);
        }
        if (points.length < 2) return;

        // Run jen jednou per (selectedId + sada bodů) — ať při Reverb update polohy
        // viewport nepřeskakuje
        const sig = selectedId + ':' + points.length + ':' + (r.objednavky_na_ceste?.length || 0);
        if (lastAutoFitRef.current === sig) return;
        lastAutoFitRef.current = sig;

        map.fitBounds(points, { padding: [60, 60], maxZoom: 15, animate: true });
    }, [selectedId, rozvozy, map]);

    // Pro každé vozidlo s aktualni_jizda_id natáhnout polyline body — paralelně.
    // Cache klíčem je sada IDček, aby se refetch nedělal při každém pohybu.
    useEffect(() => {
        const ids = rozvozy.map((r) => r.aktualni_jizda_id).filter(Boolean);
        if (ids.length === 0) {
            setTrasy({});
            return;
        }

        let cancelled = false;
        Promise.all(ids.map((jizdaId) =>
            fetch(`/api/flotila/gps/jizdy/${jizdaId}`)
                .then((r) => r.json())
                .then((data) => {
                    const trasa = Array.isArray(data?.trasa) ? data.trasa : [];
                    return { jizdaId, points: trasa.map((p) => [Number(p.lat), Number(p.lng)]) };
                })
                .catch(() => ({ jizdaId, points: [] }))
        )).then((results) => {
            if (cancelled) return;
            const map = {};
            // Přemapuj jizdaId → vozidloId
            for (const r of rozvozy) {
                if (!r.aktualni_jizda_id) continue;
                const found = results.find((x) => x.jizdaId === r.aktualni_jizda_id);
                if (found) map[r.vozidlo_id] = found.points;
            }
            setTrasy(map);
        });

        return () => { cancelled = true; };
    }, [rozvozy]);

    // Render — clear & add per re-render. Markery objednávek a polyline jsou
    // krátké, nepřechod efekty rychlejšími přístupy nepotřebujeme.
    useEffect(() => {
        if (!layerRef.current) return;
        layerRef.current.clearLayers();

        const filtered = selectedId
            ? rozvozy.filter((r) => r.vozidlo_id === selectedId)
            : rozvozy;

        for (const r of filtered) {
            const color = vozidloColor(r.vozidlo_id);

            // Markery objednávek `na cestě` — kruh s denním číslem objednávky.
            // cislo_denni je 1..999 v rámci dne, snadné odkazování v provoze
            // (volání zákazníkovi „Vaše #42 je na cestě"). Late = červený glow.
            for (const o of (r.objednavky_na_ceste || [])) {
                const num = o.cislo_denni ?? o.poradi_plan ?? '?';
                const late = (o.lateness_min || 0) > 0;
                const markerHtml = `
                    <div class="flotila-rozvoz-marker${late ? ' late' : ''}" style="background:${color};">
                        ${num}
                    </div>`;
                const m = L.marker([o.lat, o.lng], {
                    icon: L.divIcon({ html: markerHtml, className: 'flotila-rozvoz-divicon', iconSize: [22, 22], iconAnchor: [11, 11] }),
                });
                const tooltipParts = [
                    `#${o.cislo_objednavky || o.id}`,
                    o.adresa_doruceni || '',
                    o.slibeny_at ? `Slibený: ${new Date(o.slibeny_at).toLocaleTimeString('cs-CZ', { hour: '2-digit', minute: '2-digit' })}` : '',
                    o.ocekavany_doraz_at ? `ETA: ${new Date(o.ocekavany_doraz_at).toLocaleTimeString('cs-CZ', { hour: '2-digit', minute: '2-digit' })}` : '',
                    late ? `Pozdě o ${o.lateness_min} min` : '',
                ].filter(Boolean);
                m.bindTooltip(tooltipParts.join('<br/>'), { direction: 'top', offset: [0, -8] });
                layerRef.current.addLayer(m);
            }

            // Markery doručených objednávek (semi-transparent, ✓)
            for (const o of (r.objednavky_dorucene || [])) {
                const markerHtml = `
                    <div class="flotila-rozvoz-marker dorucena" style="background:${color};">
                        ✓
                    </div>`;
                const m = L.marker([o.lat, o.lng], {
                    icon: L.divIcon({ html: markerHtml, className: 'flotila-rozvoz-divicon', iconSize: [22, 22], iconAnchor: [11, 11] }),
                });
                const tip = [
                    `#${o.cislo_objednavky || o.id}`,
                    'Doručeno',
                    o.predani_sec ? `Předání: ${formatPredani(o.predani_sec)}` : '',
                ].filter(Boolean).join('<br/>');
                m.bindTooltip(tip, { direction: 'top', offset: [0, -8] });
                layerRef.current.addLayer(m);
            }

            // Plánovaná trasa — preferuj reálnou silniční geometrii z backendu
            // (Mapy.cz routing přes /aktivni-rozvozy → `planovana_trasa_geometry`).
            // Bez geometrie (routing API selhalo) fallback na vzdušné čáry mezi
            // waypointy = lepší než nic, ale prozradí chybějící data ostrými
            // hranami místo silnic.
            if (Array.isArray(r.planovana_trasa_geometry) && r.planovana_trasa_geometry.length > 1) {
                layerRef.current.addLayer(
                    L.polyline(r.planovana_trasa_geometry, {
                        color, weight: 3, opacity: 0.65, dashArray: '6, 8', lineJoin: 'round',
                    })
                );
            } else if (Number.isFinite(r.lat) && Number.isFinite(r.lng) && r.objednavky_na_ceste?.length > 0) {
                const sorted = [...r.objednavky_na_ceste]
                    .filter((o) => Number.isFinite(o.lat) && Number.isFinite(o.lng))
                    .sort((a, b) => (a.poradi_plan || 99) - (b.poradi_plan || 99));
                const planPoints = [[r.lat, r.lng], ...sorted.map((o) => [o.lat, o.lng])];
                if (planPoints.length > 1) {
                    layerRef.current.addLayer(
                        L.polyline(planPoints, { color, weight: 2.5, opacity: 0.55, dashArray: '8, 6' })
                    );
                }
            }

            // Aktuální trasa — solid polyline z bodů otevřené jízdy
            const aktualPoints = trasy[r.vozidlo_id] || [];
            if (aktualPoints.length > 1) {
                const aktualLine = L.polyline(aktualPoints, {
                    color, weight: 3, opacity: 0.85,
                });
                layerRef.current.addLayer(aktualLine);
            }
        }
    }, [rozvozy, trasy, selectedId]);

    return null;
}

/**
 * Šedé markery objednávek ve fázi „přijatá/nová" — ještě nejedou na cestě:
 *   - `v_davce=true`  → kuchař vaří   (plný šedý kruh)
 *   - `v_davce=false` → čeká ve frontě (dashed šedý kruh, světlejší)
 * Tooltip s denním číslem, kódem, adresou a časem objednání.
 *
 * Zobrazujeme jen když není vybráno konkrétní vozidlo (per-vehicle pohled
 * není o tom, co se chystá vařit).
 */
function PripravujeLayer({ pripravuje = [], selectedId }) {
    const map = useMap();
    const layerRef = useRef(null);

    useEffect(() => {
        layerRef.current = L.layerGroup().addTo(map);
        return () => {
            try { layerRef.current?.remove(); } catch {}
            layerRef.current = null;
        };
    }, [map]);

    useEffect(() => {
        if (!layerRef.current) return;
        layerRef.current.clearLayers();
        if (selectedId) return; // při výběru auta vrstvu schováme

        for (const o of pripravuje) {
            if (!Number.isFinite(o.lat) || !Number.isFinite(o.lng)) continue;
            const vDavce = !!o.v_davce;
            const num = o.cislo_denni ?? '?';
            const html = `<div class="flotila-pripravuje-marker ${vDavce ? 'v-davce' : 'waiting'}">${num}</div>`;
            const m = L.marker([o.lat, o.lng], {
                icon: L.divIcon({ html, className: 'flotila-divicon', iconSize: [22, 22], iconAnchor: [11, 11] }),
                zIndexOffset: -200,
            });
            const cas = o.cas_objednani
                ? new Date(o.cas_objednani).toLocaleTimeString('cs-CZ', { hour: '2-digit', minute: '2-digit' })
                : '';
            const denniLabel = o.cislo_denni != null ? `#${o.cislo_denni} · ` : '';
            const stav = vDavce ? 'Kuchyň vaří' : 'Čeká ve frontě';
            m.bindTooltip(
                `<b>${denniLabel}${o.cislo_objednavky || o.id}</b><br/>${o.adresa_doruceni || ''}<br/><i>${stav}${cas ? ' · objednáno ' + cas : ''}</i>`,
                { direction: 'top', offset: [0, -8] }
            );
            layerRef.current.addLayer(m);
        }
    }, [pripravuje, selectedId]);

    return null;
}

// Vizuál událostí na trase — typy odpovídají enum `stg_flotila_udalosti.typ`,
// vyhodnocuje je `FlotilaGpsAnalyzerJob` po uzavření jízdy.
const UDALOST_VIZUAL = {
    rychlost:   { color: '#EF4444', symbol: '!',  label: 'Překročení rychlosti', unit: 'km/h' },
    brzdeni:    { color: '#F97316', symbol: '↓',  label: 'Prudké brzdění',       unit: 'm/s²' },
    akcelerace: { color: '#FBBF24', symbol: '↑',  label: 'Prudká akcelerace',    unit: 'm/s²' },
    zataceni:   { color: '#A855F7', symbol: '↻',  label: 'Ostré zatáčení',       unit: '°' },
};

/**
 * Layer pro historickou jízdu — vykresluje trasu (solid polyline v base
 * barvě auta), start (▶) a end (■) markery, **pohyblivý playback marker**
 * řízený podle `playback.progress` (0..1) a markery jízdních událostí
 * (rychlost / brzdění / akcelerace / zatáčení) v místech, kde nastaly.
 * Body trasy + progress získává přes `playback` prop z `useJizdaPlayback`.
 *
 * Polyline + statické markery se kreslí jednou per jízda. Playback marker
 * je v separátním layeru a posouvá se na každou změnu progress.
 */
function HistorickaJizdaLayer({ vozidloId, playback, jizdaId }) {
    const map = useMap();
    const staticLayerRef = useRef(null);
    const objednavkyLayerRef = useRef(null);
    const udalostiLayerRef = useRef(null);
    const playbackMarkerRef = useRef(null);
    const lastJizdaIdRef = useRef(null);
    const [objednavky, setObjednavky] = useState([]);
    const [udalosti, setUdalosti] = useState([]);

    useEffect(() => {
        staticLayerRef.current = L.layerGroup().addTo(map);
        objednavkyLayerRef.current = L.layerGroup().addTo(map);
        udalostiLayerRef.current = L.layerGroup().addTo(map);
        return () => {
            if (staticLayerRef.current) staticLayerRef.current.remove();
            if (objednavkyLayerRef.current) objednavkyLayerRef.current.remove();
            if (udalostiLayerRef.current) udalostiLayerRef.current.remove();
            if (playbackMarkerRef.current) playbackMarkerRef.current.remove();
            staticLayerRef.current = null;
            objednavkyLayerRef.current = null;
            udalostiLayerRef.current = null;
            playbackMarkerRef.current = null;
        };
    }, [map]);

    // Načti objednávky té jízdy — `historie_stavu` v DB má víc stavů, ale
    // endpoint /jizdy/{id}/objednavky to filtruje podle časového rozsahu.
    useEffect(() => {
        if (!jizdaId) { setObjednavky([]); return; }
        let cancelled = false;
        fetch(`/api/flotila/gps/jizdy/${jizdaId}/objednavky`)
            .then((r) => r.json())
            .then((d) => { if (!cancelled && Array.isArray(d)) setObjednavky(d); })
            .catch(() => { if (!cancelled) setObjednavky([]); });
        return () => { cancelled = true; };
    }, [jizdaId]);

    // Načti události té jízdy. Endpoint `/jizdy/{id}` už nese `udalosti` přes
    // `with('udalosti')`, takže nemusíme volat samostatný endpoint.
    useEffect(() => {
        if (!jizdaId) { setUdalosti([]); return; }
        let cancelled = false;
        fetch(`/api/flotila/gps/jizdy/${jizdaId}`)
            .then((r) => r.json())
            .then((d) => {
                if (cancelled) return;
                const u = Array.isArray(d?.jizda?.udalosti) ? d.jizda.udalosti : [];
                setUdalosti(u);
            })
            .catch(() => { if (!cancelled) setUdalosti([]); });
        return () => { cancelled = true; };
    }, [jizdaId]);

    // Render markerů událostí — barva + symbol per typ, tooltip s hodnotou.
    useEffect(() => {
        if (!udalostiLayerRef.current) return;
        udalostiLayerRef.current.clearLayers();
        for (const u of udalosti) {
            const lat = Number(u.lat);
            const lng = Number(u.lng);
            if (!Number.isFinite(lat) || !Number.isFinite(lng)) continue;
            const viz = UDALOST_VIZUAL[u.typ] || { color: '#6B7280', symbol: '?', label: u.typ, unit: '' };
            const html = `<div class="flotila-udalost-marker" style="background:${viz.color};">${viz.symbol}</div>`;
            const m = L.marker([lat, lng], {
                icon: L.divIcon({ html, className: 'flotila-rozvoz-divicon', iconSize: [20, 20], iconAnchor: [10, 10] }),
                zIndexOffset: 500,
            });
            const hodnota = u.hodnota != null ? Number(u.hodnota).toFixed(viz.unit === 'km/h' ? 0 : 1) : '?';
            const limit = u.limit_hodnota != null ? Number(u.limit_hodnota).toFixed(viz.unit === 'km/h' ? 0 : 1) : null;
            const cas = u.zaznameno_at ? new Date(u.zaznameno_at).toLocaleTimeString('cs-CZ', { hour: '2-digit', minute: '2-digit', second: '2-digit' }) : '';
            const tip = [
                `<b>${viz.label}</b>`,
                `${hodnota} ${viz.unit}${limit ? ` (limit ${limit})` : ''}`,
                cas,
            ].filter(Boolean).join('<br/>');
            m.bindTooltip(tip, { direction: 'top', offset: [0, -8] });
            udalostiLayerRef.current.addLayer(m);
        }
    }, [udalosti]);

    // Render objednávek — všechny šedé (= prošly touto jízdou) s tooltipem.
    useEffect(() => {
        if (!objednavkyLayerRef.current) return;
        objednavkyLayerRef.current.clearLayers();
        for (const o of objednavky) {
            if (!Number.isFinite(o.lat) || !Number.isFinite(o.lng)) continue;
            const html = `<div class="flotila-rozvoz-marker dorucena" style="background:${vozidloColor(vozidloId)};">✓</div>`;
            const m = L.marker([o.lat, o.lng], {
                icon: L.divIcon({ html, className: 'flotila-rozvoz-divicon', iconSize: [22, 22], iconAnchor: [11, 11] }),
            });
            const tip = [
                `#${o.cislo_objednavky || o.id}`,
                o.adresa_doruceni || '',
                o.cas_doruceni ? `Doručeno ${new Date(o.cas_doruceni).toLocaleTimeString('cs-CZ', { hour: '2-digit', minute: '2-digit' })}` : `Stav: ${o.stav}`,
                o.predani_sec ? `Předání: ${formatPredani(o.predani_sec)}` : '',
            ].filter(Boolean).join('<br/>');
            m.bindTooltip(tip, { direction: 'top', offset: [0, -8] });
            objednavkyLayerRef.current.addLayer(m);
        }
    }, [objednavky, vozidloId]);

    // Statický layer (polyline + start/end) — recreate jen když se změní jízda
    useEffect(() => {
        if (!staticLayerRef.current) return;
        const body = playback?.body || [];
        const jizdaSignature = body.length > 0
            ? `${body[0]?.zaznameno_at || ''}|${body[body.length - 1]?.zaznameno_at || ''}`
            : null;
        if (jizdaSignature === lastJizdaIdRef.current) return;
        lastJizdaIdRef.current = jizdaSignature;

        staticLayerRef.current.clearLayers();
        if (body.length < 2) return;

        const color = vozidloColor(vozidloId);
        const points = body
            .map((p) => [Number(p.lat), Number(p.lng)])
            .filter(([la, ln]) => Number.isFinite(la) && Number.isFinite(ln));
        if (points.length < 2) return;

        const line = L.polyline(points, { color, weight: 4, opacity: 0.85 });
        staticLayerRef.current.addLayer(line);

        const startHtml = `<div class="flotila-rozvoz-marker" style="background:#10B981;">▶</div>`;
        const endHtml = `<div class="flotila-rozvoz-marker" style="background:#EF4444;">■</div>`;
        const start = L.marker(points[0], {
            icon: L.divIcon({ html: startHtml, className: 'flotila-rozvoz-divicon', iconSize: [22, 22], iconAnchor: [11, 11] }),
        }).bindTooltip('Start jízdy', { direction: 'top', offset: [0, -8] });
        const end = L.marker(points[points.length - 1], {
            icon: L.divIcon({ html: endHtml, className: 'flotila-rozvoz-divicon', iconSize: [22, 22], iconAnchor: [11, 11] }),
        }).bindTooltip('Konec jízdy', { direction: 'top', offset: [0, -8] });
        staticLayerRef.current.addLayer(start);
        staticLayerRef.current.addLayer(end);

        map.fitBounds(points, { padding: [50, 50], maxZoom: 16, animate: true });
    }, [playback?.body, vozidloId, map]);

    // Playback marker — sleduje currentPoint, hýbe se per-render
    useEffect(() => {
        if (!map) return;
        const cp = playback?.currentPoint;
        if (!cp || !Number.isFinite(cp.lat) || !Number.isFinite(cp.lng)) {
            if (playbackMarkerRef.current) {
                playbackMarkerRef.current.remove();
                playbackMarkerRef.current = null;
            }
            return;
        }
        const color = vozidloColor(vozidloId);
        if (!playbackMarkerRef.current) {
            const html = `<div class="flotila-rozvoz-marker flotila-playback-marker" style="background:${color};"></div>`;
            playbackMarkerRef.current = L.marker([cp.lat, cp.lng], {
                icon: L.divIcon({ html, className: 'flotila-rozvoz-divicon', iconSize: [22, 22], iconAnchor: [11, 11] }),
                zIndexOffset: 1000,
            }).addTo(map);
        } else {
            playbackMarkerRef.current.setLatLng([cp.lat, cp.lng]);
        }
    }, [playback?.currentPoint, vozidloId, map]);

    return null;
}

/**
 * Status badge v pravém horním rohu mapy — uživateli dává okamžitou zpětnou
 * vazbu, na co se kouká: live celé flotily, live jednoho auta, nebo historická
 * jízda. Mimo kontejner Leafletu (vrstva nad mapou).
 */
function MapStatusBadge({ selectedId, vozidla, selectedJizdaId, isDark, onCycle, isFullscreen = false, autofitPaused = false }) {
    // Tick každých 15 s, aby se hodnota stáří dat plynule aktualizovala
    // i bez příchozího Reverb eventu (vozidla zaparkovaná → žádné nové fixy).
    const [, setTick] = useState(0);
    useEffect(() => {
        const id = setInterval(() => setTick((t) => t + 1), 15000);
        return () => clearInterval(id);
    }, []);

    let label = 'Live';
    let color = '#10B981';
    let pulse = true;

    const vybrane = selectedId ? vozidla.find((v) => v.id === selectedId) : null;
    if (selectedJizdaId && vybrane) {
        label = `Historie · ${vybrane.spz}`;
        color = '#FF8A00';
        pulse = false;
    } else if (vybrane) {
        label = `Live · ${vybrane.spz}`;
    } else if (autofitPaused) {
        // Auto-fit je pauznutý (user zoomoval/panoval). Naznač přes amber dot
        // + label „Re-center", ať vidí, že klik refituje (jinak by se 60 s
        // ptal, co dělá to tlačítko vlevo nahoře).
        label = 'Re-center';
        color = '#F59E0B';
        pulse = false;
    }

    // Stáří nejnovějšího fixu: u live celé flotily přes všechna vozidla,
    // u vybraného auta jen z jeho `zaznameno`. Historie tohle nepotřebuje.
    let ago = null;
    if (!selectedJizdaId) {
        let lastTs = null;
        const zdroj = vybrane ? [vybrane] : vozidla;
        for (const v of zdroj) {
            if (!v?.zaznameno) continue;
            const t = new Date(v.zaznameno).getTime();
            if (Number.isFinite(t) && (!lastTs || t > lastTs)) lastTs = t;
        }
        if (lastTs) {
            const sec = Math.max(0, Math.round((Date.now() - lastTs) / 1000));
            if (sec < 60) ago = `${sec}s`;
            else if (sec < 3600) ago = `${Math.round(sec / 60)}m`;
            else ago = `${Math.round(sec / 3600)}h`;
        }
    }

    const clickable = !!onCycle && !selectedJizdaId && vozidla.length > 0;

    return (
        <button
            type="button"
            onClick={clickable ? onCycle : undefined}
            disabled={!clickable}
            title={clickable ? (vybrane ? 'Další vozidlo / všechna' : 'Zaměřit na první vozidlo') : undefined}
            style={{
                position: 'absolute',
                // Ve fullscreenu mapa zasahuje pod notch / Dynamic Island —
                // posuneme badge pod safe-area-inset-top + 4 px breathing.
                top: isFullscreen
                    ? 'max(12px, calc(env(safe-area-inset-top, 0px) + 4px))'
                    : 12,
                right: isFullscreen
                    ? 'max(12px, calc(env(safe-area-inset-right, 0px) + 4px))'
                    : 12,
                zIndex: 500,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                padding: '6px 10px',
                borderRadius: 7,
                background: isDark ? 'rgba(10,6,18,0.85)' : 'rgba(255,255,255,0.95)',
                border: `1px solid ${isDark ? 'rgba(255,255,255,0.10)' : 'var(--border-default)'}`,
                backdropFilter: 'blur(10px)',
                WebkitBackdropFilter: 'blur(10px)',
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: 0.4,
                textTransform: 'uppercase',
                color: isDark ? '#fff' : 'var(--text-primary)',
                fontVariantNumeric: 'tabular-nums',
                cursor: clickable ? 'pointer' : 'default',
                fontFamily: 'inherit',
                transition: 'transform 0.12s, background 0.12s',
            }}
            onMouseEnter={(e) => { if (clickable) e.currentTarget.style.transform = 'scale(1.03)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
        >
            <span
                className={pulse ? 'ms-pulse-dot' : ''}
                style={{ width: 8, height: 8, borderRadius: '50%', background: color, flexShrink: 0 }}
            />
            {label}{ago ? ` (${ago})` : ''}
        </button>
    );
}

/**
 * Mapa vozidel s Mapy.cz tiles. Reuse react-leaflet stack ze starého Flotila.jsx
 * (žádné nové dependencies).
 */
export default function MapVozidla({ vozidla = [], selectedId = null, onMarkerClick, onCycleSelected, ridiciMap, selectedJizdaId = null, playback = null, rozvozy = [], pripravuje = [], zony = [], cardStyle, isDark, isFullscreen = false, isMobile = false }) {
    const apiKey = getMapyApiKey();

    // Mapy.cz basic tiles v obou tématech — v dark přidáme CSS invert filter
    // přes wrapper class `.flotila-leaflet-dark` (viz flotila.css). Sjednoceno
    // s POS VD: jeden tile provider, dva vizuální módy. Carto Dark Matter
    // jsme opustili — invertované Mapy.cz mají lepší detail (cesty, popisky
    // Frenštát + okolí) než Carto, a vizuálně se shoduje s VD displejem.
    const tileUrl = apiKey
        ? `https://api.mapy.com/v1/maptiles/basic/256/{z}/{x}/{y}?apikey=${apiKey}`
        : 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png';
    const attribution = apiKey
        ? '<a href="https://api.mapy.com/copyright" target="_blank">© Seznam.cz a.s.</a>'
        : '© OpenStreetMap';

    // Counter inkrementovaný při kliku na LIVE badge — UserInteractionWatcher
    // ho slyší a okamžitě resetuje paused → BoundsFitter dostane šanci refit.
    const [refitSignal, setRefitSignal] = useState(0);
    const [autofitPaused, setAutofitPaused] = useState(false);

    const handleCycle = () => {
        onCycleSelected?.();
        setRefitSignal((s) => s + 1);
    };

    // Žádný cardStyle/border na mapě — sub-pixelové bordery dělají artefakty
    // při browser zoomu jiném než 100 %. Mapa je čistý clip-radius kontejner.
    return (
        <div
            // Dark filter (Mapy.cz invert + hue-rotate) aplikujeme vždy bez
            // ohledu na theme — Flotila Live má jednotný vizuál shodný s POS
            // VD displejem, ať se kuchař i dispečer dívají na ten samý obraz
            // bez kognitivního přepínání mezi „světlou" a „tmavou" mapou.
            className="flotila-leaflet-dark"
            style={{
                borderRadius: 10,
                overflow: 'hidden',
                position: 'relative',
                height: '100%',
                isolation: 'isolate',
                background: isDark ? '#0A0612' : '#f5f5f7',
            }}
        >
            <MapContainer
                center={FRENSTAT_CENTER}
                zoom={13}
                style={{ height: '100%', width: '100%', background: isDark ? '#0A0612' : '#f5f5f7' }}
                attributionControl={true}
                zoomControl={false}
            >
                <TileLayer url={tileUrl} attribution={attribution} maxZoom={19} />
                <InvalidateOnResize />
                <ZonyLayer zony={zony} />
                <BistroLayer />
                <MarkersLayer vozidla={vozidla} selectedId={selectedId} onMarkerClick={onMarkerClick} ridiciMap={ridiciMap} />
                {/* Live rozvozy + preparing zobrazujeme jen v live módu — při
                    historické jízdě by polyline plánu kolidovaly s historií. */}
                {!selectedJizdaId && <RozvozyLayer selectedId={selectedId} rozvozy={rozvozy} />}
                {!selectedJizdaId && <PripravujeLayer pripravuje={pripravuje} selectedId={selectedId} />}
                {!selectedJizdaId && (
                    <>
                        <UserInteractionWatcher onPausedChange={setAutofitPaused} refitSignal={refitSignal} />
                        <BoundsFitter
                            vozidla={vozidla}
                            rozvozy={rozvozy}
                            pripravuje={pripravuje}
                            selectedId={selectedId}
                            paused={autofitPaused}
                            isMobile={isMobile}
                        />
                    </>
                )}
                {selectedJizdaId && playback && <HistorickaJizdaLayer vozidloId={selectedId} playback={playback} jizdaId={selectedJizdaId} />}
            </MapContainer>
            <MapStatusBadge
                selectedId={selectedId}
                vozidla={vozidla}
                selectedJizdaId={selectedJizdaId}
                isDark={isDark}
                onCycle={handleCycle}
                isFullscreen={isFullscreen}
                autofitPaused={autofitPaused}
            />
        </div>
    );
}
